/* eslint-disable prefer-destructuring */
import type { KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import * as React from 'react';
import styled from '@emotion/styled';
import type { TippyProps } from '@tippyjs/react';
import Tippy from '@tippyjs/react';
import { useTippy } from '@hotjar/webapp/hooks/useTippy';
import { tokens } from '@hotjar/hotsauce';

import type { AnswerType } from './ThumbsUpDownTooltipContent';
import { ThumbsUpDownTooltipContent } from './ThumbsUpDownTooltipContent';

interface ThumbsUpDownTooltipProps extends Omit<TippyProps, 'theme' | 'content' | 'offset'> {
  questionLabel: React.ReactElement | string;
  answer: boolean | null;
  onAnswerChange: (answer: AnswerType) => void;
  children: React.ReactElement;
  distance?: number;
  footer?: React.ReactElement | string;
  isLoading?: boolean;
  offset?: number;
  callOpenTooltipOnce?: boolean;
  thumbsUpLabel: string;
  thumbsDownLabel: string;
}

const StyledTooltip = styled(Tippy)`
  text-transform: none;
  color: ${tokens.color.text_default_inverse};
  border: 0;
  font-size: 13px;
  word-break: break-word;
  word-wrap: break-word;
  background: ${tokens.color.neutral_800};
  border-radius: 4px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.25);

  .tippy-content {
    padding: 0;
    white-space: pre-line;
    text-align: center;
  }

  &[data-placement^='top'] > .tippy-arrow::before {
    border-top-color: ${tokens.color.neutral_800};
  }

  &[data-placement^='bottom'] > .tippy-arrow::before {
    border-bottom-color: ${tokens.color.neutral_800};
  }

  &[data-placement^='left'] > .tippy-arrow::before {
    border-left-color: ${tokens.color.neutral_800};
  }

  &[data-placement^='right'] > .tippy-arrow::before {
    border-right-color: ${tokens.color.neutral_800};
  }
`;

const FOCUSABLE_ELEMENT_QUERY =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

// we need to use manual focus handling instead of relying on injecting tippy
// the component is used in multiple context, including cases where fixed + sticky positions with
// translate css rule are combined and this is where positioning with fixed doesn't work as usual
// given the above, tippy is injected at the root element to make styling bulletproof
// making focus handling a little bit more difficult
const focusFirstFocusableChild = (parentElement: HTMLElement) => {
  setTimeout(() => {
    const focusableElement = parentElement.querySelector(FOCUSABLE_ELEMENT_QUERY) as HTMLElement;
    if (focusableElement && 'focus' in focusableElement) {
      focusableElement.focus();
    }
  }, 100);
};

const focusChild = (parentElement: HTMLElement, focusPrevious: boolean) => {
  const focusableChildren = Array.from(parentElement.querySelectorAll(FOCUSABLE_ELEMENT_QUERY));
  if (!focusableChildren.length) {
    return;
  }

  let elementToFocus;
  const focusedElement = document.activeElement;

  if (!focusedElement || !focusableChildren.includes(focusedElement)) {
    elementToFocus = focusableChildren[0];
  } else {
    const focusedElementIndex = focusableChildren.findIndex((child) => child === focusedElement);

    if (focusPrevious) {
      if (focusedElementIndex === 0) {
        elementToFocus = focusableChildren[focusableChildren.length - 1];
      } else {
        elementToFocus = focusableChildren[focusedElementIndex - 1];
      }
    } else if (focusedElementIndex === focusableChildren.length - 1) {
      elementToFocus = focusableChildren[0];
    } else {
      elementToFocus = focusableChildren[focusedElementIndex + 1];
    }
  }
  (elementToFocus as HTMLElement).focus();
};

export const ThumbsUpDownTooltip = ({
  questionLabel,
  answer = null,
  onAnswerChange,
  footer,
  distance = 10,
  offset = 0,
  children,
  isLoading,
  callOpenTooltipOnce = false,
  thumbsUpLabel,
  thumbsDownLabel,
  disabled,
  ...props
}: ThumbsUpDownTooltipProps): JSX.Element => {
  const tippy = useTippy();
  const [wasOpen, setWasOpen] = useState(false);
  const [cachedFocusedElement, setCachedFocusedElement] = useState<HTMLElement | null>(null);
  const contentRef = useRef<HTMLSpanElement>(null);

  // If we want to show the tooltip for specific cases and let @tippyjs/react controls the rest
  // we can't use the visible boolean props as it will turn @tippyjs/react into `controlled mode`
  // (https://github.com/atomiks/tippyjs-react#visible-boolean-controlled-mode)
  // however we can use a hacky boolean prop that we follow only when it changes value.
  useEffect(() => {
    if (callOpenTooltipOnce !== wasOpen) {
      if (callOpenTooltipOnce) {
        tippy.open();
      }
      setWasOpen(callOpenTooltipOnce);
    }
  }, [callOpenTooltipOnce, wasOpen, tippy]);

  const handleOnAnswerChange = (newAnswer: AnswerType) => {
    onAnswerChange(newAnswer);
    tippy.close();
  };

  return (
    <div>
      <StyledTooltip
        onCreate={tippy.set}
        content={
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
          <span
            role="dialog"
            ref={contentRef}
            onKeyDown={(event: KeyboardEvent<HTMLElement>) => {
              if (event.key === 'Escape') tippy.close();
              if (event.key === 'Tab') {
                if (contentRef && contentRef.current) {
                  event.preventDefault();
                  focusChild(contentRef.current, event.shiftKey);
                }
              }
            }}
          >
            <ThumbsUpDownTooltipContent
              questionLabel={questionLabel}
              answer={answer}
              onAnswerChange={handleOnAnswerChange}
              footer={footer}
              isLoading={isLoading}
              thumbsUpLabel={thumbsUpLabel}
              thumbsDownLabel={thumbsDownLabel}
            />
          </span>
        }
        interactive
        offset={[offset, distance]}
        onShow={() => {
          setCachedFocusedElement(document.activeElement as HTMLElement);
          if (contentRef && contentRef.current) focusFirstFocusableChild(contentRef.current);
        }}
        onHide={() => {
          if (cachedFocusedElement && cachedFocusedElement.focus) {
            cachedFocusedElement.focus();
          }
        }}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      >
        <span
          role="button"
          tabIndex={-1}
          onClick={() => {
            if (!disabled) tippy.open();
          }}
          onKeyDown={(event: KeyboardEvent<HTMLElement>) => {
            if (event.key === 'Enter' && !disabled) tippy.open();
          }}
        >
          {children}
        </span>
      </StyledTooltip>
    </div>
  );
};
